import {
	NotificationsButton,
	NotificationStyle,
	NotificationTypeStyles,
} from '@dialectlabs/react-ui';
import { Dispatch, RefCallback, SetStateAction, useCallback } from 'react';
import Env, {
	DriftTheme,
} from '../../environmentVariables/EnvironmentVariables';
import { DialectSolanaSdk } from '@dialectlabs/react-sdk-blockchain-solana';
import useDriftTheme from 'src/hooks/useDriftTheme';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { Bankrupt, Notification, WarningFilled } from '@drift-labs/icons';
import TopBarIconButton from '../TopBar/TopBarIconButton';
import { cj } from 'src/utils/classNames';
import { Z_INDEX } from 'src/constants/zIndex';
import { useMergeRefs } from '@floating-ui/react';
import { getColorVar } from 'src/utils/theme/colorVars';
import { WithRef } from 'src/utils/UtilityTypes';

// # dapp address here
const DAPP_ADDRESS = Env.driftDialectDappAddress;

// Configure Notification Styles
// Note: This is only for icons and link colours, other styles are in `styles/third-party/dialect.css`.
const ICON_SIZE_STYLE = { height: '20px', width: '20px' };

const DEFAULT_ALERT_STYLE: NotificationStyle = {
	Icon: <Notification style={ICON_SIZE_STYLE} />,
	iconColor: getColorVar('text-default'),
	iconBackgroundBackdropColor: getColorVar('button-secondary-bg-hover'),
};

NotificationTypeStyles['announcements'] = DEFAULT_ALERT_STYLE;
NotificationTypeStyles['filled-order'] = DEFAULT_ALERT_STYLE;
NotificationTypeStyles['liquidation-warning'] = {
	Icon: <WarningFilled style={ICON_SIZE_STYLE} />,
	iconColor: getColorVar('star-yellow'),
	iconBackgroundBackdropColor: getColorVar('star-yellow-bg'),
	linkColor: getColorVar('star-yellow'),
};
NotificationTypeStyles['liquidation-alert'] = {
	Icon: <Bankrupt style={ICON_SIZE_STYLE} />,
	iconColor: getColorVar('negative-red'),
	iconBackgroundBackdropColor: getColorVar('negative-red-secondary-bg'),
	linkColor: getColorVar('negative-red'),
};

// # Dialect Notifications Initialization
export const DialectNotificationsButton = () => {
	const theme = useDriftTheme();

	const isMobile = useIsMobileScreenSize();

	return (
		<DialectSolanaSdk
			dappAddress={DAPP_ADDRESS}
			config={{
				environment: Env.sdkEnv === 'devnet' ? 'development' : 'production',
			}}
		>
			<NotificationsButton
				theme={theme === DriftTheme.dark ? 'dark' : 'light'}
				channels={['wallet', 'telegram']}
				renderModalComponent={({ open, setOpen: _setOpen, ref, children }) => {
					if (!open) {
						return null;
					}

					return (
						<div
							ref={ref}
							className={cj(
								// Add `dialect` class here so some of Dialect's modal styling still applies.
								'dialect fixed bg-container-bg',
								isMobile ? 'inset-0' : 'inset-y-0 right-0 w-[420px]',
								Z_INDEX.modalOverlay
							)}
						>
							{children}
						</div>
					);
				}}
			>
				{({ setOpen, unreadCount, ref }) => (
					<DialectPanelTrigger
						setOpen={setOpen}
						unreadCount={unreadCount}
						ref={ref}
					/>
				)}
			</NotificationsButton>
		</DialectSolanaSdk>
	);
};

interface DialectPanelTriggerProps {
	setOpen: Dispatch<SetStateAction<boolean>>;
	unreadCount: number;
}

function DialectPanelTrigger({
	setOpen,
	unreadCount,
	ref,
}: WithRef<DialectPanelTriggerProps, HTMLButtonElement>) {
	// For some reason Dialect loads a bunch of CSS for the button that's hard to override.
	// So we remove the `dialect` class from the root (so the button is no longer targeted)
	// and add our own `.dialect-notifications` to it so we can still apply other styles.
	const callbackRef = useCallback<RefCallback<HTMLButtonElement>>((node) => {
		const dialectRoot = node?.closest('.dialect');
		dialectRoot?.classList.remove('dialect');
		dialectRoot?.classList.add('dialect-notifications');
	}, []);

	const mergedRef = useMergeRefs([ref, callbackRef]);

	return (
		<div className="relative">
			<TopBarIconButton
				id="dialect-notifications-button"
				onClick={() => setOpen((prev) => !prev)}
				icon={Notification}
				ref={mergedRef}
			/>

			{unreadCount > 0 && (
				<div
					className={cj(
						'absolute -right-1.5 -bottom-1.5 inline-flex size-4.5',
						'items-center justify-center rounded-full bg-negative-red text-3xs text-white'
					)}
				>
					<span className="pt-px">{unreadCount > 9 ? '9+' : unreadCount}</span>
				</div>
			)}
		</div>
	);
}
